import { useEffect, useState } from "react";
import { api, formatApiError, formatFCFA } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, User, Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import PhotoUpload, { toAuthUrl } from "@/components/PhotoUpload";

const STAFF_TYPES = [
  { value: "salarie", label: "Salarié" },
  { value: "benevole", label: "Bénévole" },
  { value: "entraineur", label: "Entraîneur" },
  { value: "dirigeant", label: "Dirigeant" },
  { value: "medical", label: "Staff médical" },
  { value: "administratif", label: "Staff administratif" },
];

const EMPTY = {
  first_name: "", last_name: "", staff_type: "salarie", function: "",
  date_of_birth: "", phone: "", email: "", address: "",
  hire_date: new Date().toISOString().slice(0, 10),
  contract_type: "CDI", salary: "", photo_url: "",
};

export default function RH() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/employees").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (e) => { setEditing(e); setForm({ ...EMPTY, ...e }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, salary: Number(form.salary) || 0 };
      if (editing) {
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Mis à jour");
      } else {
        await api.post("/employees", payload);
        toast.success("Membre du personnel ajouté");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/employees/${id}`);
    load();
  };

  return (
    <div data-testid="rh-page">
      <PageHeader title="Personnel & Ressources humaines"
        subtitle="Salariés, bénévoles, entraîneurs et staff du club">
        <Button onClick={startCreate} data-testid="add-employee-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouveau membre
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        {items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-12 text-center text-slate-500">
            Aucun membre du personnel enregistré.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((e) => (
              <div key={e.id} data-testid={`employee-card-${e.id}`}
                className="stat-card bg-white border border-slate-200 rounded-md p-5">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-md bg-emerald-50 flex items-center justify-center shrink-0 border border-emerald-200 overflow-hidden">
                    {e.photo_url ? (
                      <img src={toAuthUrl(e.photo_url)} alt="" className="h-full w-full object-cover" />
                    ) : <User className="h-7 w-7 text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-barlow font-bold text-lg tracking-tight truncate">
                      {e.first_name} {e.last_name}
                    </div>
                    <div className="text-xs text-slate-600 truncate flex items-center gap-1 mt-0.5">
                      <Briefcase className="h-3 w-3" /> {e.function}
                    </div>
                    <Badge className="mt-2 bg-slate-100 text-slate-700 border-slate-200 capitalize">
                      {STAFF_TYPES.find((s) => s.value === e.staff_type)?.label || e.staff_type}
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100 text-xs">
                  <div>
                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Contrat</div>
                    <div className="text-slate-900 font-medium">{e.contract_type || "—"}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Salaire</div>
                    <div className="text-emerald-700 font-semibold">{formatFCFA(e.salary || 0)}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => startEdit(e)}
                    data-testid={`edit-employee-${e.id}`} className="flex-1">
                    <Pencil className="h-3 w-3 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => del(e.id)}
                    data-testid={`delete-employee-${e.id}`}
                    className="text-red-600 hover:bg-red-50 border-red-200">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">
              {editing ? "Modifier" : "Nouveau membre du personnel"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prénom" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="emp-firstname" />
            <Field label="Nom" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="emp-lastname" />
            <SelectField label="Catégorie" value={form.staff_type} onChange={(v) => setForm({ ...form, staff_type: v })}
              options={STAFF_TYPES} testid="emp-type" />
            <Field label="Fonction" value={form.function} onChange={(v) => setForm({ ...form, function: v })} required testid="emp-function" />
            <Field label="Date de naissance" type="date" value={form.date_of_birth}
              onChange={(v) => setForm({ ...form, date_of_birth: v })} testid="emp-dob" />
            <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="emp-phone" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="emp-email" />
            <Field label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="emp-address" />
            <Field label="Date d'embauche" type="date" value={form.hire_date}
              onChange={(v) => setForm({ ...form, hire_date: v })} testid="emp-hiredate" />
            <SelectField label="Type de contrat" value={form.contract_type}
              onChange={(v) => setForm({ ...form, contract_type: v })}
              options={[
                { value: "CDI", label: "CDI" }, { value: "CDD", label: "CDD" },
                { value: "Stage", label: "Stage" }, { value: "Bénévolat", label: "Bénévolat" },
              ]} testid="emp-contract" />
            <Field label="Salaire mensuel (FCFA)" type="number" value={form.salary}
              onChange={(v) => setForm({ ...form, salary: v })} testid="emp-salary" />
            <div className="col-span-2">
              <PhotoUpload value={form.photo_url}
                onChange={(v) => setForm({ ...form, photo_url: v })}
                testid="emp-photo" label="Photo (optionnel)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-employee-button" className="bg-emerald-600 hover:bg-emerald-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
